import { ValidationError } from 'yup';

export default (t: (key: string, params?: Record<string, any>) => string) => {
    const mixed = {
        default: t('FieldsValidations.field-required'),
        required: t(
            'FieldsValidations.field-required'
        ),
        defined: t('FieldsValidations.must-be-defined'),
        notNull: t(
            'FieldsValidations.field-required'
        ),
        oneOf: (params: any) => {
            const values = params?.values;
            return t('FieldsValidations.must-be-one-of', { values: values?.join(', ') ?? '' });
        },
        notOneOf: (params: any) => {
            const values = params?.values;
            return t('FieldsValidations.must-not-be-one-of', { values: values?.join(', ') ?? '' });
        },
        notType: (params: any) => {
            const type = params?.type;
            return t('FieldsValidations.must-be-type', { type: t('types.' + type) });
        },
    };

    const string = {
        length: (params: any) => {
            const length = params?.length;
            return t('FieldsValidations.string-length', { length: length?.toString() ?? '' });
        },
        min: (params: any) => {
            const min = params?.min;
            return t('FieldsValidations.string-min', { min: min?.toString() ?? '' });
        },
        max: (params: any) => {
            const max = params?.max;
            return t('FieldsValidations.string-max', { max: max?.toString() ?? '' });
        },
        matches: (params: any) => {
            const regex = params?.regex;
            return t('FieldsValidations.string-matches', { regex: regex?.toString() ?? '' });
        },
        email: t(
            'FieldsValidations.invalid-email'
        ),
        url: t('FieldsValidations.must-be-valid-url'),
        uuid: t('FieldsValidations.must-be-valid-uuid'),
        datetime: t('FieldsValidations.must-be-valid-datetime'),
        datetime_precision: (params: any) => {
            const precision = params?.precision;
            return t('FieldsValidations.must-be-valid-datetime-precision', { precision: precision?.toString() ?? '' });
        },
        datetime_offset: t('FieldsValidations.must-be-valid-datetime-offset'),
        trim: t('FieldsValidations.must-be-trimmed'),
        lowercase: t('FieldsValidations.must-be-lowercase'),
        uppercase: t('FieldsValidations.must-be-uppercase'),
    };

    const number = {
        min: (params: any) => {
            const min = params?.min;
            return t('FieldsValidations.number-min', { min: min?.toString() ?? '' });
        },
        max: (params: any) => {
            const max = params?.max;
            return t('FieldsValidations.number-max', { max: max?.toString() ?? '' });
        },
        lessThan: (params: any) => {
            const less = params?.less;
            return t('FieldsValidations.number-less-than', { less: less?.toString() ?? '' });
        },
        moreThan: (params: any) => {
            const more = params?.more;
            return t('FieldsValidations.number-more-than', { more: more?.toString() ?? '' });
        },
        positive: t('FieldsValidations.number-positive'),
        negative: t('FieldsValidations.number-negative'),
        integer: t('FieldsValidations.number-integer'),
    };

    const date = {
        min: (params: any) => {
            const min = params?.min;
            return t('FieldsValidations.date-min', { min: min?.toString() ?? '' });
        },
        max: (params: any) => {
            const max = params?.max;
            return t('FieldsValidations.date-max', { max: max?.toString() ?? '' });
        },
    };

    const boolean = {
        isValue: (params: any) => {
            const value = params?.value;
            return t('FieldsValidations.boolean-is-value', { value: value?.toString() ?? '' });
        },
    };

    const object = {
        noUnknown: (params: any) => {
            const unknown = params?.unknown;
            return t('FieldsValidations.object-no-unknown', { unknown: unknown?.toString() ?? '' });
        },
    };

    const array = {
        min: (params: any) => {
            const min = params?.min;
            return t('FieldsValidations.array-min', { min: min?.toString() ?? '' });
        },
        max: (params: any) => {
            const max = params?.max;
            return t('FieldsValidations.array-max', { max: max?.toString() ?? '' });
        },
        length: (params: any) => {
            const length = params?.length;
            return t('FieldsValidations.array-length', { length: length?.toString() ?? '' });
        },
    };

    const tuple = {
        notType: (params: any) => {
            const { value, spec } = params;
            const typeLen = spec.types.length;
            if (Array.isArray(value)) {
                if (value.length < typeLen)
                    return t('FieldsValidations.tuple-too-few', {
                        typeLen: typeLen.toString(),
                        valueLength: value.length.toString(),
                        value: JSON.stringify(value)
                    });
                if (value.length > typeLen)
                    return t('FieldsValidations.tuple-too-many', {
                        typeLen: typeLen.toString(),
                        valueLength: value.length.toString(),
                        value: JSON.stringify(value)
                    });
            }

            return ValidationError.formatError(mixed.notType, params);
        },
    };

    return {
        mixed,
        string,
        number,
        date,
        object,
        array,
        boolean,
        tuple,
    };
}
